import axios from "axios";

import {
  useSessaoStore,
  type ParDeTokens,
  type UsuarioAutenticado,
} from "./store";
import { cliente } from "../http/cliente";

/*
 * Núcleo de autenticação local (FE-1 CP1 — ADR 0026 §Decisão D, contrato de auth).
 *
 * Três peças, em camadas:
 *  1. `fazerLogin`           — POST /api/auth/login CRU (sem interceptor), grava o par.
 *  2. `carregarUsuarioAtual` — GET /api/auth/eu pelo `cliente` (Bearer injetado).
 *  3. `autenticar`           — orquestração ATÔMICA: login + bootstrap com ROLLBACK.
 *
 * A tela (CP2) deve chamar `autenticar` — login só completa INTEIRO
 * (tokens + permissões) ou faz rollback. Não existe meio-login.
 *
 * DISCIPLINA I4 (ADR 0026 §Invariantes — sem vazamento em log): este módulo
 * NUNCA loga o erro cru do axios. O body do POST /api/auth/login carrega a
 * `senha`; o body da resposta de sucesso carrega o token. Capturamos somente o
 * `status` e o header `Retry-After` necessários e DESCARTAMOS o resto do erro —
 * nada de `console.*`, nada de serializar o `AxiosError`, nada de deixar o erro
 * cru escapar com `config`/`data`.
 */

/**
 * Resultado discriminado de uma tentativa de login. Cada falha é uma categoria
 * estável — a tela decide a mensagem. Nenhuma variante carrega token, senha nem
 * o erro cru (I4).
 */
export type ResultadoLogin =
  | { ok: true }
  | { ok: false; tipo: "credencial" }
  | { ok: false; tipo: "rate_limit"; retryAposSegundos: number | null }
  | { ok: false; tipo: "indisponivel" } // 404: auth OFF (rota não registrada)
  | { ok: false; tipo: "bootstrap_falhou" } // /eu falhou APÓS login OK
  | { ok: false; tipo: "rede" | "desconhecido" };

/** Shape EXATO do wire de `POST /api/auth/login` (snake_case do backend). */
interface RespostaLogin {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Faz parse defensivo do header `Retry-After` (segundos). Ausente → null;
 * valor não-numérico (NaN) → null; valor negativo → null (Retry-After HTTP é
 * sempre segundos NÃO-negativos). Nunca lança.
 */
function lerRetryAfter(valor: unknown): number | null {
  if (valor === null || valor === undefined) {
    return null;
  }
  const segundos = Number(valor);
  return Number.isNaN(segundos) || segundos < 0 ? null : segundos;
}

/**
 * Autentica via usuário/senha local.
 *
 * Usa axios CRU (`axios.post`), NÃO o `cliente` com interceptor (T1): um 401 de
 * login significa CREDENCIAL ERRADA, não sessão expirada — não pode disparar o
 * fluxo de refresh single-flight do interceptor. Espelha o `refresh.ts`.
 *
 * SUCESSO (200): mapeia o TokenOut do wire e grava o par no store memory-only
 * via `definirSessao`. Em falha, devolve uma categoria estável e NÃO toca o store.
 *
 * ANTI-ENUMERAÇÃO: o 401 é tratado de forma idêntica qualquer que seja o motivo
 * (email inexistente vs. senha errada). NÃO inspecionamos o corpo do 401 — é
 * sempre "credencial", espelhando o 401 byte-idêntico do backend.
 */
export async function fazerLogin(
  email: string,
  senha: string,
): Promise<ResultadoLogin> {
  try {
    // axios CRU: post direto, sem a instância `cliente` e sem o interceptor de
    // 401-retry. Caminho absoluto explícito para o proxy do Vite.
    const resposta = await axios.post<RespostaLogin>("/api/auth/login", {
      email,
      senha,
    });

    const dados = resposta.data;
    // Boundary do parse: wire (snake_case) → ParDeTokens.
    const par: ParDeTokens = {
      access_token: dados.access_token,
      refresh_token: dados.refresh_token,
      expires_in: dados.expires_in,
    };
    useSessaoStore.getState().definirSessao(par);
    return { ok: true };
  } catch (erro: unknown) {
    // I4: extrair SÓ status/header; jamais logar/serializar o erro cru (tem a
    // `senha` no body da request). Sem `console.*`, sem propagar o AxiosError.
    if (!axios.isAxiosError(erro)) {
      // Erro não-axios (bug de código) — não há resposta tipada para mapear.
      return { ok: false, tipo: "desconhecido" };
    }

    const status = erro.response?.status ?? null;

    if (status === null) {
      // Sem resposta = falha de transporte (rede, timeout, CORS).
      return { ok: false, tipo: "rede" };
    }
    if (status === 401) {
      // SEMPRE "credencial" — nunca inspeciona o corpo (anti-enumeração).
      return { ok: false, tipo: "credencial" };
    }
    if (status === 429) {
      // `Retry-After` em segundos; ausente/NaN → null (parse defensivo).
      const retryAposSegundos = lerRetryAfter(erro.response?.headers["retry-after"]);
      return { ok: false, tipo: "rate_limit", retryAposSegundos };
    }
    if (status === 404) {
      // Flag de auth OFF no backend = rota não registrada = 404 (T3).
      return { ok: false, tipo: "indisponivel" };
    }
    return { ok: false, tipo: "desconhecido" };
  }
}

/**
 * Carrega o usuário autenticado (RBAC discovery) de `GET /api/auth/eu`.
 *
 * Usa o `cliente` normal: já há token no store após o login, e o request
 * interceptor injeta o Bearer. Em sucesso, grava o usuário no store e devolve
 * `true`. Em QUALQUER falha devolve `false` — NÃO lança (o caller decide o
 * rollback). Sem log de token (o `cliente` já redige; aqui nem tocamos no erro).
 */
export async function carregarUsuarioAtual(): Promise<boolean> {
  try {
    const resposta = await cliente.get<UsuarioAutenticado>("/auth/eu");
    const dados = resposta.data;
    const usuario: UsuarioAutenticado = {
      id: dados.id,
      email: dados.email,
      nome: dados.nome,
      permissoes: dados.permissoes,
    };
    useSessaoStore.getState().definirUsuario(usuario);
    return true;
  } catch {
    // Falha de bootstrap: o caller (`autenticar`) decide o rollback. NÃO
    // tocamos no erro — I4 (nada a logar, nada a propagar).
    return false;
  }
}

/**
 * Orquestração ATÔMICA login + bootstrap (decisão-chave do plano).
 *
 * Faz `fazerLogin`; se OK, carrega o usuário (`carregarUsuarioAtual`). Se o /eu
 * FALHAR, faz ROLLBACK (`limpar` — desfaz os tokens já gravados) e devolve
 * `{ ok: false, tipo: "bootstrap_falhou" }` (a tela mostra "não foi possível
 * carregar suas permissões").
 *
 * VARIANTE DEDICADA (não reusa `indisponivel`): o /eu só falha COM auth ON
 * (500/503/rede) — nunca pelo 404 de "módulo desligado". `indisponivel` é o 404
 * de auth OFF (rota não registrada); `bootstrap_falhou` é o /eu que falhou APÓS
 * um login que deu certo. A tela (CP2) precisa distinguir os dois.
 *
 * RAZÃO DO ROLLBACK: token sem permissões passaria no guard reativo do router —
 * um limbo análogo ao replay-401 do CP-C (logado com sessão inútil). Ou o login
 * completa INTEIRO (tokens + permissões) ou desfaz tudo. SEM meio-login.
 *
 * Esta é a função que a tela (CP2) deve chamar.
 */
export async function autenticar(
  email: string,
  senha: string,
): Promise<ResultadoLogin> {
  const resultadoLogin = await fazerLogin(email, senha);
  if (!resultadoLogin.ok) {
    // Login já falhou: nada foi gravado no store, nada a desfazer.
    return resultadoLogin;
  }

  const bootstrapOk = await carregarUsuarioAtual();
  if (!bootstrapOk) {
    // ROLLBACK: desfaz os tokens que `fazerLogin` acabou de gravar — sem isto, o
    // store ficaria "logado" sem permissões (limbo). O par limpar() é total.
    useSessaoStore.getState().limpar();
    return { ok: false, tipo: "bootstrap_falhou" };
  }

  return { ok: true };
}
